import { Button } from "@/components/common/Button/Button"
import { ExcelFileViewer } from "@/components/common/ExcelViewer/ExcelFileViewer"
import { FileInfo } from "@/components/common/FileInfo/FileInfo"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { PdfIntelligenceViewer } from "@/components/common/PdfViewer/PdfIntelligenceViewer"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { SelectDocumentsDialog } from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  useOnboardingQuestionnaireData,
  useOnboardingQuestionnaireDocument,
} from "@/features/onboarding"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import {
  questionnaireApiCalls,
  useExportFilledPdf,
  useGetQuestionnaireFilterConfig,
  useUpdateQuestionnaireStatus,
} from "@/services/api/questionnaireService"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { downloadFile, downloadFileFromUrl } from "@/utils/downloadUtils"
import {
  getFileExtension,
  isExcelFileName,
  isPdfFileName,
} from "@/utils/fileUtils"
import { isLimitError } from "@/utils/limitErrors"
import { createDownloadDocumentName } from "@/utils/questionnaire/fileName"
import { IconCheck, IconFileDownload } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

export const PreviewPage: React.FC = () => {
  const { translate } = useTranslation()
  const excelIntelligenceEnabled = useFeatureFlag("EXCEL_INTELLIGENCE")
  const pdfIntelligenceEnabled = useFeatureFlag("PDF_INTELLIGENCE")
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const navigate = useNavigate()
  const {
    data: questionnaire,
    refetch: refetchQuestionnaire,
    isPending: isQuestionnairePending,
    isError: isQuestionnaireError,
    error: questionnaireError,
    isDemo: isDemoQuestionnaire,
  } = useOnboardingQuestionnaireData(questionnaireId)

  // Cached detail can outlive a refetch that came back 404, so the data alone does not say the
  // questionnaire still exists. Everything that depends on it waits on this instead.
  const questionnaireAvailable =
    !!questionnaire && !isQuestionnaireNotFound(questionnaireError)

  const isPdfQuestionnaire =
    !!questionnaire && isPdfFileName(questionnaire.fileName)
  const hasPositionData = !!questionnaire?.hasPositionData

  const canExportAnsweredPdf =
    pdfIntelligenceEnabled && isPdfQuestionnaire && hasPositionData

  const showExcelPreview =
    !!questionnaire &&
    excelIntelligenceEnabled &&
    isExcelFileName(questionnaire.fileName) &&
    hasPositionData

  const shouldFetchDocument =
    !!questionnaireId && questionnaireAvailable && !canExportAnsweredPdf
  const {
    data: questionnaireDocument,
    refetch: refetchQuestionnaireDocument,
    isPending: isDocumentPending,
    isFetching: isDocumentFetching,
    isError: isDocumentError,
  } = useOnboardingQuestionnaireDocument(questionnaireId, shouldFetchDocument, {
    demoDocumentVariant: "answered",
  })

  const {
    data: exportedFilledPdf,
    isPending: isExportFilledPdfPending,
    isFetching: isExportFilledPdfFetching,
    isError: isExportFilledPdfError,
  } = useExportFilledPdf(
    questionnaireId,
    !!questionnaireId && questionnaireAvailable && canExportAnsweredPdf,
  )

  const previewDocumentSource = canExportAnsweredPdf
    ? exportedFilledPdf
    : questionnaireDocument

  const isPreviewPending = canExportAnsweredPdf
    ? isExportFilledPdfPending
    : isDocumentPending

  const {
    mutate: updateQuestionnaireStatus,
    isPending: isUpdateStatusPending,
    isSuccess: isUpdateStatusSuccess,
    isError: isUpdateStatusError,
    error: updateStatusError,
    data: updateStatusResponse,
  } = useUpdateQuestionnaireStatus()

  const isDemoFlow =
    isDemoQuestionnaire || questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID

  const previewDocumentUrl = canExportAnsweredPdf
    ? exportedFilledPdf
    : questionnaireDocument

  const [isDownloadPending, setIsDownloadPending] = useState(false)
  const [isSelectDocumentsOpen, setIsSelectDocumentsOpen] = useState(false)

  const { data: questionnaireFilterConfig } = useGetQuestionnaireFilterConfig(
    questionnaireId,
    taggingFlowEnabled && questionnaireAvailable,
  )

  const selectedDocumentsCount = taggingFlowEnabled
    ? (questionnaireFilterConfig?.matchedCount ?? 0)
    : 0
  const selectedCategoryFilters = taggingFlowEnabled
    ? (questionnaireFilterConfig?.categoryFilters ?? [])
    : []
  const selectedTagIds = selectedCategoryFilters.flatMap(
    (categoryFilter) => categoryFilter.tagIds,
  )
  const excludeExpired = taggingFlowEnabled
    ? (questionnaireFilterConfig?.excludeExpired ?? false)
    : false

  const isDocumentLoading =
    shouldFetchDocument && (isDocumentPending || isDocumentFetching)
  const isExportLoading =
    canExportAnsweredPdf &&
    (isExportFilledPdfPending || isExportFilledPdfFetching)

  const isPreviewLoading =
    (isDocumentLoading || isExportLoading) && !isDownloadPending

  const isPreviewError = canExportAnsweredPdf
    ? isExportFilledPdfError
    : isDocumentError

  const [questionnaireStatus, setQuestionnaireStatus] =
    useQuestionnaireStatusNavigation(questionnaire, {
      enableAutoRedirect: !isDemoFlow,
    })

  // Handle questionnaire load errors and status sync
  useEffect(() => {
    if (isQuestionnaireError) {
      if (!isQuestionnaireNotFound(questionnaireError)) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
      }
      return
    }

    if (questionnaire) {
      setQuestionnaireStatus(questionnaire.status)
    }
  }, [
    isQuestionnaireError,
    questionnaireError,
    questionnaire,
    setQuestionnaireStatus,
    translate,
  ])

  // Show toast on document fetch errors
  useEffect(() => {
    if (isDocumentError) {
      toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
    }
  }, [isDocumentError, translate])

  useEffect(() => {
    if (isExportFilledPdfError) {
      toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
    }
  }, [isExportFilledPdfError, translate])

  // Handle questionnaire status update results
  useEffect(() => {
    const handleQuestionnaireUpdate = async () => {
      if (isUpdateStatusError) {
        // The axios interceptor already showed the limit toast with its upgrade action
        if (!isLimitError(updateStatusError)) {
          toast.error(translate("QUESTIONNAIRE_ANSWERS/STATUS_UPDATE_ERROR"))
        }
      } else if (isUpdateStatusSuccess && updateStatusResponse) {
        await refetchQuestionnaire()
        setQuestionnaireStatus(updateStatusResponse.status)
      }
    }
    handleQuestionnaireUpdate()
  }, [
    isUpdateStatusSuccess,
    updateStatusResponse,
    isUpdateStatusError,
    updateStatusError,
    setQuestionnaireStatus,
    refetchQuestionnaire,
    translate,
  ])

  const handleEditAnswersButtonClick = () => {
    updateQuestionnaireStatus({
      questionnaireId,
      status: QuestionnaireStatus.EditAnswers,
    })
  }

  const handleCompleteProcessButtonClick = () => {
    trackEvent(AnalyticsEvent.QUESTIONNAIRE_COMPLETED, {
      questionnaire_id: questionnaireId,
    })

    navigate("/")
    toast.success(translate("QUESTIONNAIRE_PREVIEW/TOAST_COMPLETED_PROCESS"))
  }

  const handleDownload = async () => {
    setIsDownloadPending(true)
    try {
      const fileName = createDownloadDocumentName(questionnaire)

      let freshUrl: string | undefined

      if (canExportAnsweredPdf) {
        freshUrl = await questionnaireApiCalls.exportFilledPdf(questionnaireId)
      } else {
        const refetchResult = await refetchQuestionnaireDocument()
        freshUrl = refetchResult.data
      }

      if (!freshUrl) {
        toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
        return
      }

      if (canExportAnsweredPdf) {
        try {
          await downloadFileFromUrl(freshUrl, fileName)
        } catch {
          toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
          return
        }
      } else {
        downloadFile(freshUrl, fileName)
      }

      const fileExtension = getFileExtension(fileName)

      trackEvent(AnalyticsEvent.QUESTIONNAIRE_DOWNLOADED, {
        questionnaire_id: questionnaireId,
        file_type: fileExtension,
      })
    } catch {
      toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
    } finally {
      setIsDownloadPending(false)
    }
  }

  // Render preview content based on current state
  const renderPreviewContent = () => {
    // Show spinner during any fetch/refetch
    if (isPreviewLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner />
        </div>
      )
    }

    // No URL available (error or not loaded)
    if (!previewDocumentUrl) {
      return (
        <div className="flex h-full items-center justify-center">
          <Typography variant="textM">
            {translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR")}
          </Typography>
        </div>
      )
    }

    // Excel preview
    if (showExcelPreview) {
      return (
        <div className="flex h-full w-full flex-col p-M">
          <div className="w-full flex-1">
            <ExcelFileViewer file={previewDocumentUrl} />
          </div>
        </div>
      )
    }

    // PDF preview
    return (
      <div className="relative h-full">
        <div className="absolute inset-0">
          <PdfIntelligenceViewer
            fileUrl={previewDocumentUrl}
            highlightBoxesArray={[]}
          />
        </div>
      </div>
    )
  }

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  return (
    <MainLayout
      contentCenter={renderPreviewContent()}
      contentRight={
        <div className="space-y-4" id="preview-buttons">
          {taggingFlowEnabled ? (
            <SelectDocumentsDialog
              questionnaireId={questionnaireId}
              open={isSelectDocumentsOpen}
              onOpenChange={setIsSelectDocumentsOpen}
              initialSelectedTagIds={selectedTagIds}
              initialHideExpired={excludeExpired}
              readOnly
              readOnlyCategoryFilters={selectedCategoryFilters}
            />
          ) : null}

          <Typography as="h2" variant="textM">
            {translate("QUESTIONNAIRE/YOUR_QUESTIONNAIRE")}
          </Typography>
          <div>
            <FileInfo
              fileTitle={questionnaire?.fileName || ""}
              status={questionnaireStatus}
              isLoading={
                isUpdateStatusPending ||
                isQuestionnairePending ||
                isDownloadPending
              }
              selectedDocumentsCount={
                taggingFlowEnabled ? selectedDocumentsCount : 0
              }
              onSelectDocuments={
                taggingFlowEnabled
                  ? () => setIsSelectDocumentsOpen(true)
                  : undefined
              }
            ></FileInfo>
          </div>
          <Button
            variantColor="dark"
            title={translate("QUESTIONNAIRE_PREVIEW/EDIT_ANSWERS_BUTTON")}
            label={translate("QUESTIONNAIRE_PREVIEW/EDIT_ANSWERS_BUTTON")}
            onClick={handleEditAnswersButtonClick}
            variantSize="medium"
            isFullWidth={true}
            isDisabled={isUpdateStatusPending}
          />
          <Button
            elementId="download-file-button"
            variantColor="dark"
            title={translate("QUESTIONNAIRE_PREVIEW/DOWNLOAD_BUTTON")}
            label={translate("QUESTIONNAIRE_PREVIEW/DOWNLOAD_BUTTON")}
            IconRight={IconFileDownload}
            onClick={handleDownload}
            isDisabled={
              isPreviewPending ||
              isPreviewError ||
              !previewDocumentSource ||
              isDownloadPending
            }
            variantSize="medium"
            isFullWidth={true}
          />
          <Button
            variantColor="yellow"
            title={translate("QUESTIONNAIRE_PREVIEW/COMPLETE_PROCESS_BUTTON")}
            label={translate("QUESTIONNAIRE_PREVIEW/COMPLETE_PROCESS_BUTTON")}
            onClick={handleCompleteProcessButtonClick}
            variantSize="medium"
            isFullWidth={true}
            IconRight={IconCheck}
          />
        </div>
      }
    ></MainLayout>
  )
}
